import { createFileRoute, Link } from "@tanstack/react-router";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { createVendor, getMyVendor } from "@/lib/vendor.functions";

export const Route = createFileRoute("/_authenticated/postani-prodajalec")({
  head: () => ({ meta: [{ title: "Odpri svojo trgovinico — kupish.si" }] }),
  component: Page,
});

function Page() {
  const queryClient = useQueryClient();
  const create = useServerFn(createVendor);
  const fetchVendor = useServerFn(getMyVendor);

  const { data: existingVendor, isLoading: checking } = useQuery({
    queryKey: ["my-vendor"],
    queryFn: () => fetchVendor(),
  });

  const [storeName, setStoreName] = useState("");
  const [city, setCity] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await create({ data: { store_name: storeName, city: city || null, description: description || null } });
      queryClient.setQueryData(["my-vendor"], {
        id: result.id,
        slug: result.slug,
        store_name: storeName,
        city: city || null,
        description: description || null,
        status: "inactive",
        shipping_fee: 0,
      });
      await queryClient.invalidateQueries({ queryKey: ["my-vendor"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  if (checking) {
    return <div className="mx-auto max-w-xl px-6 py-24 text-muted-foreground">Nalagam…</div>;
  }

  if (existingVendor) {
    return (
      <section className="mx-auto max-w-xl px-6 py-16 text-center">
        <div className="eyebrow">Vaša trgovina</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-3">{existingVendor.store_name}</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Vaša trgovina je že ustvarjena. Urejajte jo ali si jo oglejte.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          {existingVendor.slug && (
            <Link
              to="/prodajalci/$slug"
              params={{ slug: existingVendor.slug }}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center border border-border px-6 py-3 text-sm hover:bg-muted"
            >
              Predogled moje trgovine →
            </Link>
          )}
          <Link
            to="/trgovina"
            className="inline-flex items-center justify-center bg-primary text-primary-foreground px-6 py-3 text-sm hover:opacity-90"
          >
            Uredi mojo trgovino →
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-xl px-6 py-16">
      <div className="eyebrow">Pridružite se</div>
      <h1 className="font-serif text-4xl md:text-5xl mt-3">Odprite svojo trgovino</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Prvi <strong className="text-foreground">3 mesece so brezplačni</strong> — trgovina je objavljena takoj, letno najemnino 240 € poravnate šele po izteku brezplačnega obdobja.
      </p>

      <form className="mt-10 space-y-5" onSubmit={onSubmit}>
        <div>
          <label className="text-xs eyebrow">Ime trgovine *</label>
          <input required minLength={2} maxLength={100} value={storeName} onChange={(e) => setStoreName(e.target.value)}
            className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
        </div>
        <div>
          <label className="text-xs eyebrow">Mesto</label>
          <input maxLength={100} value={city} onChange={(e) => setCity(e.target.value)}
            className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
        </div>
        <div>
          <label className="text-xs eyebrow">Opis trgovine</label>
          <textarea rows={5} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)}
            className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
        </div>
        {error && <div className="text-sm text-destructive">{error}</div>}
        <button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground py-3 text-sm hover:opacity-90 disabled:opacity-60">
          {loading ? "Ustvarjam…" : "Ustvari trgovino"}
        </button>
      </form>
    </section>
  );
}
